import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  contractConsumptionApi,
  ContractConsumptionDashboard,
  ContractConsumptionFilters,
} from '@/lib/api/reports';

// Blob responses hide the JSON error body; read it back to show the API message
async function errorMessage(err: any, fallback: string): Promise<string> {
  const data = err?.response?.data;
  if (data instanceof Blob) {
    try {
      return JSON.parse(await data.text()).message || fallback;
    } catch {
      return fallback;
    }
  }
  return data?.message || fallback;
}

export function useContractConsumption(filters: ContractConsumptionFilters | null) {
  const [data, setData] = useState<ContractConsumptionDashboard | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const contractId = filters?.contractId;
  const startMonth = filters?.startMonth;
  const endMonth = filters?.endMonth;

  useEffect(() => {
    if (!contractId) {
      setData(null);
      return;
    }
    let cancelled = false;
    const load = async () => {
      setIsLoading(true);
      try {
        const result = await contractConsumptionApi.get({ contractId, startMonth, endMonth });
        if (!cancelled) setData(result);
      } catch (err) {
        if (cancelled) return;
        setData(null);
        toast.error(await errorMessage(err, 'Erro ao carregar o consumo do contrato'));
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [contractId, startMonth, endMonth]);

  const exportPdf = useCallback(async () => {
    if (!contractId) return;
    try {
      setIsExporting(true);
      const blob = await contractConsumptionApi.downloadPdf({ contractId, startMonth, endMonth });
      const safeNumber = (data?.contract.number ?? 'contrato').replace(/[^a-zA-Z0-9_-]/g, '_');
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `consumo-contrato-${safeNumber}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(await errorMessage(err, 'Erro ao gerar o PDF'));
    } finally {
      setIsExporting(false);
    }
  }, [contractId, startMonth, endMonth, data?.contract.number]);

  return { data, isLoading, isExporting, exportPdf };
}
